import { useRouter } from 'next/router';

import { Box, Button, LoadingMask, Text, TextButton } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../../constants/FreemiumConstants';

import {
  emitStartActivateFreemiumBottomEvent,
  emitStartActivateFreemiumTopEvent,
  emitStartActivatePremiumBottomEvent,
  emitStartActivatePremiumTopEvent,
} from '../../../../../analytics/events/payAndPublish';
import { useAuth } from '../../../../AuthProvider';

import {
  ACTIVATE_MULTIPLEX_TRIGGER_OBJECT,
  type ActivateMultiplexTriggerObject,
} from '../../../../../analytics/events/payAndPublish';

export const COMPONENT_SECTION_CONTEXT = {
  TOP: 'top',
  BOTTOM: 'bottom',
} as const;

type Props = {
  onPublishClick: (isPremium: boolean, multiplexTriggerObject?: ActivateMultiplexTriggerObject) => void;
  componentSectionContext?: string;
};

const PremiumProductOptionsSimple = ({
  onPublishClick,
  componentSectionContext = COMPONENT_SECTION_CONTEXT.TOP,
}: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails, isLoading } = useGetListingDetails(listingAlias);
  const products = listingDetails?.freemium?.products ?? [];
  const premiumProduct = products.find((p) => p.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM);

  const csEventParams = { currentProperty: { listingAlias }, user: user ?? {} };

  const handlePublishClick = (isPremium: boolean) => {
    const isTop = componentSectionContext === COMPONENT_SECTION_CONTEXT.TOP;
    if (isPremium) {
      isTop ? emitStartActivatePremiumTopEvent(csEventParams) : emitStartActivatePremiumBottomEvent(csEventParams);
    } else {
      isTop ? emitStartActivateFreemiumTopEvent(csEventParams) : emitStartActivateFreemiumBottomEvent(csEventParams);
    }
    if (isPremium) {
      // Labels this button for multiplex activation clickstream; PayAndPublish only uses it when
      // isInMultiplexListingFlow (stored for PremiumPaymentGateway, otherwise ignored).
      onPublishClick(true, ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITH_PREMIUM);
    } else {
      onPublishClick(false, ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITHOUT_PREMIUM);
    }
  };

  return (
    <Box data-testid="PremiumProductOptionsSimple" css={{ textAlign: 'center' }}>
      <Box css={{ marginBottom: '600' }}>
        <Text textStyle="heading-xs" asChild css={{ letterSpacing: '0.05em' }}>
          <p>{premiumProduct?.price}</p>
        </Text>
        <Text textStyle="body-sm" color="text.subtle" asChild>
          <p>One-time payment</p>
        </Text>
      </Box>

      <div>
        <Button
          tone="brand"
          emphasis="filled"
          fluid={{ base: true, md: false }}
          css={{ display: 'block', margin: '0 auto', marginBottom: '600' }}
          onClick={() => handlePublishClick(true)}
          data-testid={`PremiumProductOptionsSimple-PublishWithPremiumButton-${componentSectionContext}`}
        >
          Publish with premium
        </Button>

        <LoadingMask iconSize="sm" loading={isLoading}>
          <TextButton
            asChild
            onClick={() => handlePublishClick(false)}
            data-testid="PremiumProductOptionsSimple-PublishWithoutPremiumButton"
          >
            <div>No thanks, publish without premium</div>
          </TextButton>
        </LoadingMask>
      </div>
    </Box>
  );
};

export default PremiumProductOptionsSimple;
